import React, { useEffect, useState, lazy, Suspense } from 'react';

import { isDevEnv } from '~/modules/helpers/environment';

// Lazy load the dialog to avoid loading it until needed
const FeatureFlagDialog = lazy(() => import('./FeatureFlagDialog'));

/**
 * Feature Flag Manager
 *
 * A developer tool for managing feature flag overrides in non-production environments.
 * Opens with Shift + Ctrl/Cmd + F keyboard shortcut.
 *
 * Features:
 * - Toggle feature flags on/off via localStorage or URL params
 * - Search and filter flags by name or category
 * - Quick toggle button for instant toggle + reload
 * - URL mode for sharing flag configurations
 *
 * Only renders in dev environments (localhost, staging, etc.)
 */
export const FeatureFlagManager = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasBeenOpened, setHasBeenOpened] = useState(false);

    useEffect(() => {
        // Only register keyboard handler in dev environments
        if (!isDevEnv()) {
            return;
        }

        const handleKeyPress = (event) => {
            // Toggle with Shift + Ctrl + F (or Shift + Cmd + F on Mac)
            if (
                event.shiftKey &&
                (event.ctrlKey || event.metaKey) &&
                (event.key === 'F' || event.key === 'f' || event.code === 'KeyF')
            ) {
                event.preventDefault();
                setHasBeenOpened(true);
                setIsOpen((prev) => !prev);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, []);

    // Don't render anything in production or if never opened
    if (!isDevEnv() || !hasBeenOpened) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <FeatureFlagDialog
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
            />
        </Suspense>
    );
};

export default FeatureFlagManager;
